import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface AdminDropdownConfigResponse {
  llm_models: string[];
  ocr_modes: string[];
  tokens?: number[];
  defaults?: {
    llm_model?: string;
    ocr_mode?: string;
    tokens?: number;
  };
}

export interface DocumentCompareDifference {
  path: string;
  current: unknown;
  target: unknown;
}

export interface DocumentCompareResponse {
  status: 'SAME' | 'DIFFERENT' | 'TARGET_NOT_FOUND';
  current: Record<string, unknown>;
  target: Record<string, unknown> | null;
  differences: DocumentCompareDifference[];
  ignored_keys: string[];
}

@Injectable({
  providedIn: 'root'
})
export class DocumentModelService {
  private apiUrl = `${environment.apiBaseUrl}/api/v1`;
  
  // Cache for per-client document model summaries
  private documentSummaryCache = new Map<string, Observable<any[]>>();

  private get formHeaders() {
    return {
      'Content-Type': 'application/x-www-form-urlencoded',
      'accept': 'application/json'
    };
  }

  private get jsonHeaders() {
    return {
      'accept': 'application/json'
    };
  }

  constructor(private http: HttpClient) {}

  /**
   * GET /api/v1/admin/clients
   */
  getClients(): Observable<string[]> {
    return this.http.get<string[]>(
      `${this.apiUrl}/admin/clients`,
      { headers: this.jsonHeaders }
    );
  }

  /**
   * GET /api/v1/admin/documents
   * @param clientId - Client ID to filter document models for specific client (required)
   */
  getAdminDocuments(clientId: string): Observable<any[]> {
    const params = new HttpParams().set('client_id', clientId);
    return this.http.get<any[]>(
      `${this.apiUrl}/admin/documents`,
      { headers: this.jsonHeaders, params }
    );
  }

  /**
   * GET /api/v1/admin/dropdown-config
   */
  getAdminDropdownConfig(): Observable<AdminDropdownConfigResponse> {
    return this.http.get<AdminDropdownConfigResponse>(
      `${this.apiUrl}/admin/dropdown-config`,
      { headers: this.jsonHeaders }
    );
  }

  /**
   * GET /api/v1/eval/dashboard
   */
  getDashboard(): Observable<any> {
    return this.http.get<any>(
      `${this.apiUrl}/eval/dashboard`,
      { headers: this.jsonHeaders }
    );
  }

  /**
   * GET /api/v1/admin/documents/summary
   * Lightweight summary of all document models for the admin landing page.
   * Results are cached per-client to support client switching.
   * @param clientId - Optional client ID to filter by specific client
   */
  getDocumentModelsSummary(clientId?: string): Observable<any[]> {
    const cacheKey = clientId || 'default';
    
    // Return cached result if available
    if (this.documentSummaryCache.has(cacheKey)) {
      return this.documentSummaryCache.get(cacheKey)!;
    }

    let params = new HttpParams();
    
    if (clientId) {
      params = params.set('client_id', clientId);
    }
    
    // Make HTTP request with caching
    const request$ = this.http.get<any[]>(
      `${this.apiUrl}/admin/documents/summary`,
      { 
        headers: this.jsonHeaders,
        params: params.keys().length > 0 ? params : undefined
      }
    ).pipe(
      shareReplay(1) // Cache the result for concurrent subscribers
    );
    
    // Store in cache
    this.documentSummaryCache.set(cacheKey, request$);
    return request$;
  }

  /**
   * Force-refresh the document model summary, bypassing the cache.
   * Call after creating a document model, uploading a schema, or landing on a page
   * that displays this summary, so it never shows stale data.
   */
  refreshDocumentModelsSummary(clientId?: string): Observable<any[]> {
    this.documentSummaryCache.delete(clientId || 'default');
    return this.getDocumentModelsSummary(clientId);
  }

  /**
   * POST /api/v1/data/document_keys
   */
  getDocumentKeys(documentModel: string): Observable<any> {
    const body = `document_model=${encodeURIComponent(documentModel)}&include_internal_key=false&response_format=json&include_extraction_schema=true`;
    return this.http.post<any>(
      `${this.apiUrl}/data/document_keys`,
      body,
      { headers: this.formHeaders }
    );
  }

  /**
   * GET /api/v1/document/{document_model}/field_schema
   */
  getFieldSchemas(documentModel: string): Observable<any> {
    return this.http.get<any>(
      `${this.apiUrl}/document/${encodeURIComponent(documentModel)}/field_schema`,
      { headers: this.jsonHeaders }
    );
  }

  /**
   * POST /api/v1/admin/documents/form
   */
  createDocumentModel(params: {
    clientName: string;
    documentModel: string;
    extractionSchema: string;
    promptKeys: string;
    computeSchema?: string | null;
  }): Observable<any> {
    const body = new URLSearchParams();
    body.set('client_name', params.clientName);
    body.set('document_model', params.documentModel);
    body.set('extraction_schema', params.extractionSchema);
    body.set('prompt_keys', params.promptKeys);
    body.set('computed_schema', params.computeSchema ?? 'null');

    return this.http.post(
      `${this.apiUrl}/admin/documents/form`,
      body.toString(),
      { headers: this.formHeaders }
    );
  }

  /**
   * POST /api/v1/admin/documents/form
   * Flexible form payload for admin modal create flow.
   */
  createDocumentModelForm(formValues: Record<string, string>): Observable<any> {
    const body = new URLSearchParams();
    Object.entries(formValues).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        body.set(key, value);
      }
    });

    return this.http.post(
      `${this.apiUrl}/admin/documents/form`,
      body.toString(),
      { headers: this.formHeaders }
    );
  }

  /**
   * POST /api/v1/document/{document_model}/{field_name}
   * Create a new field definition
   */
  createField(documentModel: string, fieldName: string, body: {
    field_name: string;
    document_model: string;
    field_schema: any;
    computed_schema: any;
    is_internal: boolean;
  }): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/document/${encodeURIComponent(documentModel)}/${encodeURIComponent(fieldName)}`,
      body,
      { headers: { 'Content-Type': 'application/json', 'accept': 'application/json' } }
    );
  }

  /**
   * PUT /api/v1/document/{document_model}/{field_name}
   * Update a field schema version
   */
  updateField(documentModel: string, fieldName: string, body: {
    change_reason: string;
    field_schema: any;
    computed_schema?: any;
    field_datatype?: string;
    feature?: string;
  }): Observable<any> {
    return this.http.put(
      `${this.apiUrl}/document/${encodeURIComponent(documentModel)}/${encodeURIComponent(fieldName)}`,
      body,
      { headers: { 'Content-Type': 'application/json', 'accept': 'application/json' } }
    );
  }

  /**
   * DELETE /api/v1/document/{documentModel}/{fieldName}
   */
  deleteField(documentModel: string, fieldName: string): Observable<any> {
    return this.http.delete(
      `${this.apiUrl}/document/${encodeURIComponent(documentModel)}/${encodeURIComponent(fieldName)}`,
      { headers: this.jsonHeaders }
    );
  }

  /**
   * GET /api/v1/admin/documents/{document_model}
   * Download document model configuration as JSON file
   */
  getDocumentModel(documentModel: string): Observable<Blob> {
    return this.http.get(
      `${this.apiUrl}/admin/documents/${encodeURIComponent(documentModel)}`,
      { 
        headers: this.jsonHeaders,
        responseType: 'blob'
      }
    );
  }

  /**
   * GET /api/v1/admin/documents/{document_model}/compare
   * Compare this environment's document model config with another environment's.
   */
  compareDocumentModel(documentModel: string, targetEnvName: string): Observable<DocumentCompareResponse> {
    return this.http.get<DocumentCompareResponse>(
      `${this.apiUrl}/admin/documents/${encodeURIComponent(documentModel)}/compare`,
      {
        headers: this.jsonHeaders,
        params: { target_env_name: targetEnvName }
      }
    );
  }

  /**
   * PUT /api/v1/admin/documents/{document_model}/push
   * Push this environment's document model config to another environment.
   */
  pushDocumentModel(documentModel: string, targetEnvName: string): Observable<any> {
    const body = new URLSearchParams();
    body.set('target_env_name', targetEnvName);

    return this.http.put(
      `${this.apiUrl}/admin/documents/${encodeURIComponent(documentModel)}/push`,
      body.toString(),
      { headers: this.formHeaders }
    );
  }

  /**
   * PUT /api/v1/admin/documents/{document_model}/pull
   * Pull another environment's document model config into this environment.
   */
  pullDocumentModel(documentModel: string, targetEnvName: string): Observable<any> {
    const body = new URLSearchParams();
    body.set('target_env_name', targetEnvName);

    return this.http.put(
      `${this.apiUrl}/admin/documents/${encodeURIComponent(documentModel)}/pull`,
      body.toString(),
      { headers: this.formHeaders }
    );
  }

  /**
   * GET /api/v1/admin/document/{document_model}
   * Fetch raw document model data as JSON for admin edit modal prefill.
   */
  getDocumentModelConfig(documentModel: string): Observable<any> {
    return this.http.get<any>(
      `${this.apiUrl}/admin/document/${encodeURIComponent(documentModel)}`,
      { headers: this.jsonHeaders }
    );
  }

  /**
   * PUT /api/v1/admin/document/{document_model}
   * Update document model metadata from admin edit modal.
   */
  updateAdminDocumentMetadata(documentModel: string, payload: Record<string, any>): Observable<any> {
    return this.http.put(
      `${this.apiUrl}/admin/document/${encodeURIComponent(documentModel)}`,
      payload,
      { headers: { 'Content-Type': 'application/json', 'accept': 'application/json' } }
    );
  }

  /**
   * PUT /api/v1/admin/documents/{document_model}
   * Update document model configuration from JSON file
   */
  updateDocumentModel(documentModel: string, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.put(
      `${this.apiUrl}/admin/documents/${encodeURIComponent(documentModel)}`,
      formData,
      { headers: { 'accept': 'application/json' } }
    );
  }
}
